import React from 'react';
import { styled } from '@compiled/react';
import { easeOut } from '@atlaskit/motion/curves';
import { durations } from '@atlaskit/motion/durations';
import { token } from '@atlaskit/tokens';
import { gridSize, layers } from '@atlassian/jira-common-styles/src/main.tsx';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TooltipWrapper = styled.div({
	alignItems: 'baseline',
	display: 'flex',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 1.3,
	paddingBottom: token('space.050'),
	paddingTop: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ShortcutWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.neutral.bold.hovered'),
	borderRadius: '2px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 1.2,
	marginLeft: token('space.050'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	paddingTop: '1px',
	paddingRight: token('space.100'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	paddingBottom: '1px',
	paddingLeft: token('space.100'),
});

export const TooltipNode = ({ text, char }: { text: string; char: string }) => (
	<TooltipWrapper>
		<span>{text}</span>
		<ShortcutWrapper>{char}</ShortcutWrapper>
	</TooltipWrapper>
);

export type ButtonProps = {
	isSidebarCollapsed: boolean;
};

const buttonInteractionStyles = {
	':hover': {
		backgroundColor: token('color.background.selected.bold'),
		color: token('color.text.inverse'),
		opacity: 1,
	},
	':active, :focus': {
		backgroundColor: token('color.background.selected.bold.hovered'),
		color: token('color.text.inverse'),
		opacity: 1,
		outline: 'none',
	},
};

// For UI consistency, these styles are mostly copied from the 'atlassian-frontend' ResizeButton
// component in https://bitbucket.org/atlassian/atlassian-frontend/src/master/packages/design-system/page-layout/src/components/resize-control/resize-button.tsx?at=master
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const StyledResizeButton = styled.button<ButtonProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface.overlay'),
	border: 0,
	borderRadius: '50%',
	boxShadow: token('elevation.shadow.overflow'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: ({ isSidebarCollapsed }) => (isSidebarCollapsed ? 1 : 0),
	cursor: 'pointer',
	position: 'absolute',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 3}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 3}px`,
	top: token('space.600'),
	left: token('space.negative.150'),
	outline: 0,
	padding: 0,
	transform: 'translateY(-50%)',
	/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766 */
	transition: `
	background-color ${durations.small}ms linear,
	color ${durations.small}ms linear,
	opacity ${durations.medium}ms ${easeOut}
	`,
	/* eslint-enable @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766 */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: layers.rightSidebar,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...buttonInteractionStyles,
});

// The resize button clickable area is a little small, so we use this div to expand the clickable
// area for a better UX
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ExpandedHitArea = styled.div({
	position: 'absolute',
	top: token('space.negative.100'),
	right: token('space.negative.150'),
	bottom: token('space.negative.100'),
	left: token('space.negative.150'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ResizeBoundaryButton = styled.button({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.border'),
	border: 0,
	cursor: 'pointer',
	position: 'absolute',
	outline: 0,
	padding: 0,
	height: '100%',
	width: '2px',
	/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766 */
	transition: `
	background-color ${durations.small}ms linear,
	color ${durations.small}ms linear,
	`,
	/* eslint-enable @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766 */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...buttonInteractionStyles,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ResizeBoundaryGrabArea = styled.div({
	position: ' absolute',
	top: token('space.negative.100'),
	right: token('space.negative.300'),
	bottom: 0,
	left: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: layers.rightSidebar,
});
